/**
 * Routes.
 *
 * Public: sign-in and certificate verification - the latter because a regulator
 * holding a printed report needs to check it without an account.
 * Everything else sits behind the shell, which enforces the session.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { FeedbackFab } from "@/components/FeedbackFab";
import { AppShell } from "@/components/layout/AppShell";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const RequestsPage = lazy(() => import("@/pages/RequestsPage"));
const RequestDeletionsPage = lazy(() => import("@/pages/RequestDeletionsPage"));
const RequestDetailPage = lazy(() => import("@/pages/RequestDetailPage"));
const NewRequestPage = lazy(() => import("@/pages/NewRequestPage"));
const AddSamplePage = lazy(() => import("@/pages/AddSamplePage"));
const SampleLinkPage = lazy(() => import("@/pages/SampleLinkPage"));
const DisposalPage = lazy(() => import("@/pages/DisposalPage"));
const DisposalRunPage = lazy(() => import("@/pages/DisposalRunPage"));
const ReceptionPage = lazy(() => import("@/pages/ReceptionPage"));
const WorklistPage = lazy(() => import("@/pages/WorklistPage"));
const BatchAssignPage = lazy(() => import("@/pages/BatchAssignPage"));
const HandoversPage = lazy(() => import("@/pages/HandoversPage"));
const WorksheetPage = lazy(() => import("@/pages/WorksheetPage"));
const BenchBinderPage = lazy(() => import("@/pages/BenchBinderPage"));
const CertificatesPage = lazy(() => import("@/pages/CertificatesPage"));
const CertificateDetailPage = lazy(() => import("@/pages/CertificateDetailPage"));
const EquipmentPage = lazy(() => import("@/pages/EquipmentPage"));
const MonitoringPage = lazy(() => import("@/pages/MonitoringPage"));
const MonitoringAssignmentsPage = lazy(() => import("@/pages/MonitoringAssignmentsPage"));
const MonitoringEntryPage = lazy(() => import("@/pages/MonitoringEntryPage"));
const EquipmentFormPage = lazy(() => import("@/pages/EquipmentFormPage"));
const EquipmentDetailPage = lazy(() => import("@/pages/EquipmentDetailPage"));
const InstrumentDataPage = lazy(() => import("@/pages/InstrumentDataPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const InventoryItemPage = lazy(() => import("@/pages/InventoryItemPage"));
const InventoryItemFormPage = lazy(() => import("@/pages/InventoryItemFormPage"));
const InventoryLotPage = lazy(() => import("@/pages/InventoryLotPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const DocumentDetailPage = lazy(() => import("@/pages/DocumentDetailPage"));
const WorksheetTemplatesPage = lazy(() => import("@/pages/WorksheetTemplatesPage"));
const WorksheetRunsPage = lazy(() => import("@/pages/WorksheetRunsPage"));
const WorksheetRunPage = lazy(() => import("@/pages/WorksheetRunPage"));
const FormPreviewPage = lazy(() => import("@/pages/FormPreviewPage"));
const WorksheetDesignPage = lazy(() => import("@/pages/WorksheetDesignPage"));
const DocumentFormPage = lazy(() => import("@/pages/DocumentFormPage"));
const ValidationsPage = lazy(() => import("@/pages/ValidationsPage"));
const ValidationDetailPage = lazy(() => import("@/pages/ValidationDetailPage"));
const ValidationFormPage = lazy(() => import("@/pages/ValidationFormPage"));
const ConnectionsPage = lazy(() => import("@/pages/ConnectionsPage"));
const ConnectionFormPage = lazy(() => import("@/pages/ConnectionFormPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const MethodFormPage = lazy(() => import("@/pages/MethodFormPage"));
const CalculationFormPage = lazy(() => import("@/pages/CalculationFormPage"));
const SampleTypeFormPage = lazy(() => import("@/pages/SampleTypeFormPage"));
const AnalysisProfileFormPage = lazy(() => import("@/pages/AnalysisProfileFormPage"));
const AnalysisServiceFormPage = lazy(() => import("@/pages/AnalysisServiceFormPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const OversightPage = lazy(() => import("@/pages/OversightPage"));
const DepartmentsAdminPage = lazy(() => import("@/pages/admin/DepartmentsAdminPage"));
const DepartmentFormPage = lazy(() => import("@/pages/admin/DepartmentFormPage"));
const LabelsAdminPage = lazy(() => import("@/pages/admin/LabelsAdminPage"));
const RegistersAdminPage = lazy(() => import("@/pages/admin/RegistersAdminPage"));
const WorkflowAdminPage = lazy(() => import("@/pages/admin/WorkflowAdminPage"));
const UsersAdminPage = lazy(() => import("@/pages/admin/UsersAdminPage"));
const DelegationsPage = lazy(() => import("@/pages/DelegationsPage"));
const SettingsAdminPage = lazy(() => import("@/pages/admin/SettingsAdminPage"));
const BackupsAdminPage = lazy(() => import("@/pages/admin/BackupsAdminPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SignInPage = lazy(() => import("@/pages/SignInPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const DiscrepanciesPage = lazy(() => import("@/pages/DiscrepanciesPage"));
const OutsourcingPage = lazy(() => import("@/pages/OutsourcingPage"));
const RetestsPage = lazy(() => import("@/pages/RetestsPage"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
// Public, like certificate verification: the person writing in has no account.
const PublicFeedbackPage = lazy(() => import("@/pages/PublicFeedbackPage"));
const ExternalLabsPage = lazy(() => import("@/pages/ExternalLabsPage"));
const ExternalLabFormPage = lazy(() => import("@/pages/ExternalLabFormPage"));
// The portal is a different application behind the same bundle: its own shell, its
// own session, no staff route reachable from it. Sharing the API client, the
// components and the palette is worth more than a second build would be, and a
// client-facing form that drifts from the staff one is how the two stop comparing.
const PortalSignInPage = lazy(() => import("@/pages/portal/PortalSignInPage"));
const PortalSignupPage = lazy(() => import("@/pages/portal/PortalSignupPage"));
const PortalNewRequestPage = lazy(() =>
  import("@/pages/portal/PortalRequestPages").then((m) => ({ default: m.PortalNewRequestPage })),
);
const PortalRequestDetailPage = lazy(() =>
  import("@/pages/portal/PortalRequestPages").then((m) => ({
    default: m.PortalRequestDetailPage,
  })),
);
const PortalDeclareSamplePage = lazy(() =>
  import("@/pages/portal/PortalRequestPages").then((m) => ({
    default: m.PortalDeclareSamplePage,
  })),
);
const PortalHomePage = lazy(() => import("@/pages/portal/PortalPage"));
const PortalCustomersPage = lazy(() => import("@/pages/portal/PortalCustomersPage"));
const PortalConfirmationPage = lazy(() =>
  import("@/pages/portal/PortalPage").then((m) => ({ default: m.PortalConfirmationPage })),
);
const PortalOutsourcingPage = lazy(() =>
  import("@/pages/portal/PortalOutsourcingPage").then((m) => ({ default: m.default })),
);

function PageFallback() {
  return (
    <div className="space-y-3">
      <div className="skeleton h-8 w-56" />
      <div className="skeleton h-40 w-full" />
    </div>
  );
}

export function App() {
  return (
    <>
      {/* Outside the routes on purpose: the same corner on every screen, staff,
          portal and public alike. The button hides itself on the feedback page. */}
      <FeedbackFab />
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        {/* Landed on from an emailed link, so it must work with no session. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/verify/:code" element={<VerifyPage />} />
        {/* Anybody the centre has served may say how it went. No session, no
            shell: the address is printed on the receipt and the notice board. */}
        <Route path="/feedback/new" element={<PublicFeedbackPage />} />

        {/* Clients. Outside the staff shell entirely - not a tab inside it. */}
        <Route path="/portal/sign-in" element={<PortalSignInPage />} />
        {/* The same screen as the staff one, and deliberately the same component:
            setting a password is one form. The path is what says which door to
            offer afterwards, because a path survives the mail clients that
            rewrite links and a query parameter does not. */}
        <Route path="/portal/reset-password" element={<ResetPasswordPage />} />
        <Route path="/portal/signup" element={<PortalSignupPage />} />
        <Route path="/portal/requests/new" element={<PortalNewRequestPage />} />
        {/* The same path shape as the desk's `/requests/:id/samples/new`, because it
            is the same form: one schema, filtered to the questions about a container,
            answered by the client instead of by the reception officer. */}
        <Route
          path="/portal/requests/:id/samples/new"
          element={<PortalDeclareSamplePage />}
        />
        <Route path="/portal/requests/:id" element={<PortalRequestDetailPage />} />
        <Route path="/portal/customers" element={<PortalCustomersPage />} />
        <Route path="/portal" element={<PortalHomePage />} />
        <Route path="/portal/confirmations/:id" element={<PortalConfirmationPage />} />
        <Route path="/portal/outsourcings/:id" element={<PortalOutsourcingPage />} />

        <Route element={<AppShell />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/requests/new" element={<NewRequestPage />} />
          <Route path="/requests/:id" element={<RequestDetailPage />} />
          {/* A request frozen pending a deletion decision is invisible everywhere
              else: it does not leave the request list and its status does not
              change. Declared after /requests/:id deliberately — React Router ranks
              a static segment above a dynamic one, so the order here is
              documentation, not behaviour. */}
          <Route path="/requests/deletions" element={<RequestDeletionsPage />} />

          {/* A consignment that arrives in parts belongs to one request, not two. */}
          <Route path="/requests/:id/samples/new" element={<AddSamplePage />} />
          {/* What the QR label on a container points at. */}
          <Route path="/samples/:code" element={<SampleLinkPage />} />
          <Route path="/reception" element={<ReceptionPage />} />
          {/* A held sample is not on the reception queue any more - it is not awaiting
              reception, it is awaiting its owner. It needs a queue of its own. */}
          <Route path="/reception/confirmations" element={<DiscrepanciesPage />} />
          {/* The portion moving from the store to a bench. Under /reception because
              that is the desk that acts on the queue, and read from the other end by
              the analyst who has to sign for what arrives. */}
          <Route path="/reception/handovers" element={<HandoversPage />} />
          {/* The retention register and the runs that empty it. Under /reception
              because the desk that took the sample in is the desk that lets it go. */}
          <Route path="/reception/disposal" element={<DisposalPage />} />
          <Route path="/reception/disposal/:id" element={<DisposalRunPage />} />
          <Route path="/worklist" element={<WorklistPage />} />
          <Route path="/worklist/assign" element={<BatchAssignPage />} />
          <Route path="/bench-binder" element={<BenchBinderPage />} />
          {/* Work that has left the building. Its own queue for the same reason the
              confirmations queue exists: an outsourced analysis is off every worklist,
              and without a screen it depends on somebody remembering. */}
          <Route path="/outsourcing" element={<OutsourcingPage />} />
          <Route path="/catalog/external-labs" element={<ExternalLabsPage />} />
          {/* Its own page, like the instrument and method forms: an address, a
              contact and an accreditation are more than a dialog holds. */}
          <Route path="/catalog/external-labs/new" element={<ExternalLabFormPage />} />
          <Route path="/catalog/external-labs/:id/edit" element={<ExternalLabFormPage />} />
          <Route path="/analyses/:id" element={<WorksheetPage />} />
          <Route path="/worksheet-runs" element={<WorksheetRunsPage />} />
          <Route path="/worksheet-runs/:id" element={<WorksheetRunPage />} />
          {/* One sample's analysis on a sheet: its team, its hand-over, its
              instruments, a retest. Review and approval stay on the sheet. */}
          <Route path="/worksheet-runs/:id/samples/:testId" element={<WorksheetPage runSample />} />
          {/* The retest register. Its own screen rather than a filter on the worklist:
              the questions it answers - how often does this centre re-run work, and
              what causes it - are asked from outside the bench and mostly about the
              past. */}
          <Route path="/retests" element={<RetestsPage />} />
          {/* What customers said, read by the Director, the lead executives and
              quality. `/feedback/new` above is the public page and is matched
              first; `/feedback/:id` opens the inbox on one message. */}
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/feedback/:id" element={<FeedbackPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />
          <Route path="/certificates/:id" element={<CertificateDetailPage />} />
          <Route path="/equipment" element={<EquipmentPage />} />
          {/* Forms put on people to fill on a schedule. Not under /equipment: a log may be
              about a cold room, and the person filling it need not see the register. */}
          <Route path="/monitoring" element={<MonitoringPage />} />
          <Route path="/monitoring/assignments" element={<MonitoringAssignmentsPage />} />
          <Route path="/monitoring/entries/:id" element={<MonitoringEntryPage />} />
          {/* Registration and editing are their own pages: thirty fields do not belong
              in a dialog, and an instrument's record deserves an address. */}
          <Route path="/equipment/new" element={<EquipmentFormPage />} />
          <Route path="/equipment/:id/edit" element={<EquipmentFormPage />} />
          <Route path="/equipment/:id" element={<EquipmentDetailPage />} />
          <Route path="/equipment/calibrations/:calibrationId" element={<EquipmentDetailPage />} />
          <Route path="/instrument-data" element={<InstrumentDataPage />} />

          {/* The store. `/new` before `/:id` is documentation rather than
              behaviour — React Router ranks a static segment above a dynamic
              one — but the order says which the reader should expect to win. */}
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventory/items/new" element={<InventoryItemFormPage />} />
          <Route path="/inventory/items/:id" element={<InventoryItemPage />} />
          <Route path="/inventory/items/:id/edit" element={<InventoryItemFormPage />} />
          {/* A lot has an address of its own because a recall investigation is
              something one person sends another a link to. */}
          <Route path="/inventory/lots/:id" element={<InventoryLotPage />} />

          {/* Document control. `/new` and `/:id/edit` are the register entry;
              revisions are raised from the document itself, because a revision
              only means anything next to the one it replaces. */}
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/new" element={<DocumentFormPage />} />
          <Route path="/documents/worksheets" element={<WorksheetTemplatesPage />} />
          <Route path="/documents/:id" element={<DocumentDetailPage />} />
          <Route path="/documents/:id/edit" element={<DocumentFormPage />} />
          <Route path="/documents/:id/preview/:revisionId" element={<FormPreviewPage />} />
          <Route path="/documents/:id/design/:revisionId" element={<WorksheetDesignPage />} />

          {/* Method validation. `/new` before `/:id` for the same reason as
              everywhere else here: React Router ranks the static segment above
              the dynamic one, and the order is documentation of that. */}
          <Route path="/validations" element={<ValidationsPage />} />
          <Route path="/validations/new" element={<ValidationFormPage />} />
          <Route path="/validations/:id" element={<ValidationDetailPage />} />
          {/* Configuring how an instrument is read is its own screen: the transport,
              the credentials and the conversation are too many fields for a dialog. */}
          <Route path="/instruments/connections" element={<ConnectionsPage />} />
          <Route path="/instruments/connections/new" element={<ConnectionFormPage />} />
          <Route path="/instruments/connections/:id/edit" element={<ConnectionFormPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          {/* A method carries thirty fields and a parameter table; it gets a page. */}
          <Route path="/catalog/methods/new" element={<MethodFormPage />} />
          <Route path="/catalog/methods/:id/edit" element={<MethodFormPage />} />
          {/* No `/edit` suffix: a published calculation is read-only, so the detail page
              and the editor are the same screen showing what the person may do. */}
          <Route path="/catalog/calculations/new" element={<CalculationFormPage />} />
          <Route path="/catalog/calculations/:id" element={<CalculationFormPage />} />
          {/* A service carries its methods, its matrices and its commercial terms;
              like a method, it gets a page rather than a dialog. */}
          <Route path="/catalog/services/new" element={<AnalysisServiceFormPage />} />
          <Route path="/catalog/services/:id/edit" element={<AnalysisServiceFormPage />} />
          <Route path="/catalog/profiles/new" element={<AnalysisProfileFormPage />} />
          <Route path="/catalog/profiles/:id/edit" element={<AnalysisProfileFormPage />} />
          <Route path="/catalog/sample-types/new" element={<SampleTypeFormPage />} />
          <Route path="/catalog/sample-types/:id/edit" element={<SampleTypeFormPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          {/* The Authority's report on the centre. Inside the staff shell because the
              Authority's officers sign in the same way everyone else does; the page
              itself is gated on `report.oversight`, which no laboratory role holds. */}
          <Route path="/oversight" element={<OversightPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          {/* Reading the structure needs no permission — every screen refers to it.
              The form renders read-only without `admin.departments`. */}
          <Route path="/admin/departments" element={<DepartmentsAdminPage />} />
          <Route path="/admin/departments/new" element={<DepartmentFormPage />} />
          <Route path="/admin/departments/:id/edit" element={<DepartmentFormPage />} />
          <Route path="/admin/labels" element={<LabelsAdminPage />} />
          <Route path="/admin/registers" element={<RegistersAdminPage />} />
          <Route path="/admin/workflows" element={<WorkflowAdminPage />} />
          <Route path="/admin/users" element={<UsersAdminPage />} />
          {/* Listed in the administration section of the sidebar, but addressed
              outside /admin: the screen is open to everybody, and the address is
              the one the delegation notification and its email already point at. */}
          <Route path="/delegations" element={<DelegationsPage />} />
          <Route path="/admin/settings" element={<SettingsAdminPage />} />
          <Route path="/admin/backups" element={<BackupsAdminPage />} />
          <Route path="/search" element={<RequestsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}
